import { useEffect, useMemo, useState } from 'react';
import { adminApi, downloadBlob } from '@/lib/api';
import { IndianRupee, Calendar, Download, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

const COLORS = ['#C97A5E', '#5C4033', '#D2B48C', '#4A7c59', '#2A9D8F', '#E76F51'];

export default function ReportsCollectionsPage() {
  const [period, setPeriod] = useState('month');
  const [staffFilter, setStaffFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [modeFilter, setModeFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [collectionData, setCollectionData] = useState(null);
  const [chartKey, setChartKey] = useState(0);
  const [staff, setStaff] = useState([]);

  useEffect(() => {
    fetchCollectionReport();
  }, [period, staffFilter, sourceFilter, modeFilter]);

  useEffect(() => {
    setChartKey((prev) => prev + 1);
  }, [collectionData]);

  const fetchCollectionReport = async () => {
    setLoading(true);
    try {
      const [response, staffRes] = await Promise.all([
        adminApi.getCollectionSummary(period, {
          staff_id: staffFilter !== 'all' ? staffFilter : undefined,
          collection_source: sourceFilter !== 'all' ? sourceFilter : undefined,
          payment_mode: modeFilter !== 'all' ? modeFilter : undefined,
        }),
        adminApi.getSalesStaff(),
      ]);
      setCollectionData(response.data);
      setStaff(staffRes.data || []);
    } catch (error) {
      toast.error('Failed to fetch collection report');
    } finally {
      setLoading(false);
    }
  };

  const collectionDailyData = useMemo(
    () => (collectionData?.daily_collections || []).map((item) => ({
      ...item,
      amount: Number(item.amount || 0),
      count: Number(item.count || 0),
    })),
    [collectionData]
  );

  const collectionMethodData = useMemo(
    () => Object.entries(collectionData?.method_breakdown || {}).map(([name, data]) => ({
      name,
      value: Number(data.amount || 0),
      count: Number(data.count || 0),
    })),
    [collectionData]
  );

  const customerRows = useMemo(() => collectionData?.customer_rows || [], [collectionData]);
  const staffSummaryRows = useMemo(() => collectionData?.staff_summary || [], [collectionData]);

  const downloadExcel = async (dataset) => {
    try {
      const res = await adminApi.downloadAdminDatasetExcel(dataset, {
        period,
        staff_id: staffFilter !== 'all' ? staffFilter : undefined,
        collection_source: sourceFilter !== 'all' ? sourceFilter : undefined,
        payment_mode: modeFilter !== 'all' ? modeFilter : undefined,
      });
      downloadBlob(res.data, `${dataset}.xlsx`);
    } catch (error) {
      toast.error('Failed to download Excel');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-collections-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">Reports · Collections</h1>
          <p className="text-[#A8A8A8] mt-1">Payments and outstanding balances</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => downloadExcel('collections-customers')}>
            <Download className="w-4 h-4 mr-2" /> Customer Excel
          </Button>
          <Button variant="outline" onClick={() => downloadExcel('collections-staff')}>
            <Download className="w-4 h-4 mr-2" /> Staff Excel
          </Button>
          <Calendar className="w-5 h-5 text-[#A8A8A8]" />
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40 border-[#E5E5E5]" data-testid="period-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Today</SelectItem>
                <SelectItem value="week">Last 7 Days</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
              </SelectContent>
            </Select>
          <Select value={staffFilter} onValueChange={setStaffFilter}>
            <SelectTrigger className="w-44 border-[#E5E5E5]">
              <SelectValue placeholder="Staff" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Staff</SelectItem>
              {staff.map((row) => (
                <SelectItem key={row.id} value={row.id}>{row.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-44 border-[#E5E5E5]">
              <SelectValue placeholder="Collection Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="direct_company">Received by Company</SelectItem>
              <SelectItem value="staff_collected">Collected by Staff</SelectItem>
            </SelectContent>
          </Select>
          <Select value={modeFilter} onValueChange={setModeFilter}>
            <SelectTrigger className="w-40 border-[#E5E5E5]">
              <SelectValue placeholder="Mode" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Modes</SelectItem>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="upi">UPI</SelectItem>
              <SelectItem value="bank">Bank</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-sm text-[#A8A8A8]">Total Collected</p>
            <p className="text-2xl font-bold text-[#4A7c59]">{formatCurrency(collectionData?.total_collected)}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-sm text-[#A8A8A8]">Collection Count</p>
            <p className="text-2xl font-bold text-[#2D2420]">{collectionData?.collection_count || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-sm text-[#A8A8A8]">Outstanding</p>
            <p className="text-2xl font-bold text-[#E76F51]">{formatCurrency(collectionData?.total_outstanding)}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-sm text-[#A8A8A8]">Pending Handover</p>
            <p className="text-2xl font-bold text-[#C97A5E]">{formatCurrency(collectionData?.pending_handover_total)}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">Daily Collections</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart key={`collections-${chartKey}`} data={collectionDailyData.slice(-14)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Line type="monotone" dataKey="amount" stroke="#4A7c59" strokeWidth={2} dot={{ fill: '#4A7c59' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">Collection by Payment Method</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={collectionMethodData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({ name }) => name}
                  >
                    {collectionMethodData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-3">
              {collectionMethodData.map((method, idx) => (
                <div key={method.name} className="flex items-center justify-between p-3 bg-[#FAFAF9] rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                    <span className="font-medium text-[#2D2420] capitalize">{method.name}</span>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-[#2D2420]">{formatCurrency(method.value)}</p>
                    <p className="text-xs text-[#A8A8A8]">{method.count} transactions</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">Customer-wise Collections</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Customer</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Area / Staff</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Opening Due</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Collected</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Direct</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Staff</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Pending Handover</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Closing Due</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E5E5]">
                {customerRows.map((row) => (
                  <tr key={row.customer_id}>
                    <td className="py-3 px-4">
                      <p className="font-medium text-[#2D2420]">{row.customer_name}</p>
                      <p className="text-xs text-[#A8A8A8]">{row.shop_name}</p>
                    </td>
                    <td className="py-3 px-4 text-sm text-[#2D2420]">
                      <p>{row.area || '-'}</p>
                      <p className="text-xs text-[#A8A8A8]">{(row.staff_names || []).join(', ') || '-'}</p>
                    </td>
                    <td className="py-3 px-4 text-[#2D2420]">{formatCurrency(row.opening_due)}</td>
                    <td className="py-3 px-4 font-semibold text-[#4A7c59]">{formatCurrency(row.amount_collected)}</td>
                    <td className="py-3 px-4 text-[#2D2420]">{formatCurrency(row.direct_company_amount)}</td>
                    <td className="py-3 px-4 text-[#2D2420]">{formatCurrency(row.staff_collected_amount)}</td>
                    <td className="py-3 px-4 text-[#C97A5E]">{formatCurrency(row.pending_handover_amount)}</td>
                    <td className="py-3 px-4 text-[#E76F51]">{formatCurrency(row.closing_due)}</td>
                  </tr>
                ))}
                {!customerRows.length ? (
                  <tr><td colSpan={8} className="py-8 text-center text-[#A8A8A8]">No collections found for the selected filters.</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">Staff Collection vs Settlement</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Staff</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Collected Today</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Collected Month</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Settled Month</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Pending Handover</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E5E5]">
                {staffSummaryRows.map((row) => (
                  <tr key={row.staff_id}>
                    <td className="py-3 px-4 font-medium text-[#2D2420]">{row.staff_name}</td>
                    <td className="py-3 px-4 text-[#2D2420]">{formatCurrency(row.collected_today)}</td>
                    <td className="py-3 px-4 text-[#2D2420]">{formatCurrency(row.collected_month)}</td>
                    <td className="py-3 px-4 text-[#4A7c59]">{formatCurrency(row.settled_month)}</td>
                    <td className="py-3 px-4 text-[#C97A5E]">{formatCurrency(row.pending_handover_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
